import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FolderSearchIcon, MedalIcon, ShieldAlertIcon, ShieldCheckIcon, TimerIcon, UserMinusIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { pageSamples as samples } from './samples';

// JUDO GOTOWE™ copy (Polish). Single source: judo-gotowe/PRODUCTO.md.
// Product mockup (WebP 2048², transparent) on Higgsfield CDN — see judo-gotowe/media/MOCKUP.md.
export const PRODUCT_IMAGE = 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/348efb83-3813-4baa-a998-548d05de91a1.webp';
export const DISCLAIMER = 'To nie jest oficjalna publikacja Polskiego Związku Judo. W razie urazu lub bólu skonsultuj się z lekarzem.';
export const heroGuarantees = ['Natychmiastowy dostęp do pobrania', 'Jednorazowa płatność', 'Bez subskrypcji', '7 dni gwarancji satysfakcji'];
export const stats = [{
  value: '6',
  label: 'Cyfrowych tomów dla trenera judo'
}, {
  value: '240',
  label: 'Stron gotowych materiałów'
}, {
  value: '50',
  label: 'Gotowych treningów w banku zajęć'
}, {
  value: '100',
  label: 'Gier i zabaw judo'
}, {
  value: '5',
  label: 'Bonusów w zestawie'
}];
export const differenceStats = [{
  value: 'Grupy 4–15 lat',
  label: 'Przedszkolaki, dzieci i młodzicy w jednym systemie'
}, {
  value: '3 poziomy',
  label: 'W każdych zajęciach – jedna mata i nikt nie czeka'
}, {
  value: 'PDF do druku',
  label: 'Na ekranie albo na kartce przy macie'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Niedzielny wieczór na planowaniu',
  text: 'Zamiast odpocząć, siedzisz nad konspektem na poniedziałkowy trening – i znowu zaczynasz od pustej kartki.'
}, {
  title: 'Jedna mata, trzy poziomy',
  text: 'Na zajęciach są 5-latki i 13-latki, biały pas i zielony. Jednym jest za trudno, inni czekają i się nudzą.'
}, {
  title: 'Dzieci się nudzą i rozrabiają',
  text: 'Gdy ćwiczenie trwa za długo, uwaga znika, a grupa zaczyna biegać po macie.'
}, {
  title: 'Bezpieczeństwo cały czas w głowie',
  text: 'Ukemi, odległości między parami, rzuty dobrane do wieku – wszystkiego musisz pilnować sam.'
}, {
  title: 'Ciągle te same ćwiczenia',
  text: 'Brakuje nowych gier i zabaw, a szukanie pomysłów w internecie zajmuje godziny.'
}, {
  title: 'Egzaminy, turnieje i rodzice',
  text: 'Karty egzaminacyjne, dyplomy i wiadomości do rodziców – wszystko robisz po treningach.'
}];
export const afterItems = ['Plan roczny wrzesień–czerwiec dla 3 grup wiekowych', '12 zajęć minuta po minucie i bank 50 gotowych treningów', 'Każde zajęcia w 3 poziomach – cała grupa ćwiczy naraz', '100 gier i zabaw judo na rozgrzewkę, ukemi i parter', 'Ukemi i bezpieczeństwo na pierwszym miejscu', 'Technika krok po kroku i program klubowy 6 kyu → 1 kyu', 'Testy sprawności, karty egzaminacyjne i dyplomy', 'Gotowe wiadomości do rodziców', 'Wszystko w PDF do druku'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Niedzielny wieczór = planowanie',
  text: 'Czy w niedzielę wieczorem zamiast odpoczywać układasz plan poniedziałkowego treningu?'
}, {
  icon: UsersIcon,
  title: 'Grupa mieszana 4–15 lat',
  text: 'Czy na jednej macie masz przedszkolaków, dzieci i młodzików – i różne kolory pasów?'
}, {
  icon: UserMinusIcon,
  title: 'Dzieci się nudzą',
  text: 'Czy część grupy stoi w kolejce, czeka albo przestaje słuchać po kilku minutach?'
}, {
  icon: ShieldAlertIcon,
  title: 'Bezpieczeństwo i ukemi',
  text: 'Czy chcesz mieć pewność, że każde ćwiczenie i każdy rzut są dobrane do wieku dzieci?'
}, {
  icon: FolderSearchIcon,
  title: 'Kończą Ci się pomysły',
  text: 'Czy szukasz nowych rozgrzewek i zabaw, które wciągną całą grupę?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Egzaminy na pasy i rodzice',
  text: 'Czy przed egzaminem i turniejem znowu przygotowujesz karty, protokoły i wiadomości od zera?'
}];

// Spine colours mirror the box (navy, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Tom 01',
  level: 'Planowanie',
  title: 'Plan roczny i 12 gotowych zajęć minuta po minucie',
  tagline: 'Wiesz, co robić na macie – od września do czerwca.',
  intro: 'Plan roczny wrzesień–czerwiec dla trzech grup (4–6, 7–10 i 11–15 lat), plan tygodnia przy 1, 2 i 3 treningach oraz 12 zajęć rozpisanych minuta po minucie.',
  items: ['Plan roczny dla 3 grup wiekowych', 'Plan tygodnia: 1, 2 lub 3 treningi', 'Struktura zajęć 45 i 60 minut', '12 zajęć minuta po minucie', 'Cel, sprzęt, wskazówki trenera i 3 poziomy', 'Mapa maty i lista kontrolna przed zajęciami'],
  outro: 'Otwierasz, wybierasz zajęcia i wchodzisz na matę z planem w 5 minut.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 02',
  level: 'Bank zajęć',
  title: 'Bank zajęć: 50 gotowych treningów',
  tagline: 'Gotowy trening na każdy tydzień sezonu.',
  intro: '50 treningów według grup wiekowych (przedszkolaki, 7–10 i 11–15 lat) i bloków tematycznych – każdy z celem, czasem, przebiegiem i 3 poziomami.',
  items: ['Przedszkolaki 4–6 lat', 'Dzieci 7–10 i 11–15 lat', 'Ukemi, parter i rzuty', 'Gry i sprawność', 'Przed egzaminem i przed turniejem', 'Zajęcia pokazowe dla rodziców'],
  outro: 'Nie musisz już wymyślać treningu od zera.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 03',
  level: 'Gry i zabawy',
  title: '100 gier i zabaw judo',
  tagline: 'Ruch, śmiech i judo w jednym.',
  intro: 'Gry ruchowe na rozgrzewkę, zabawy z ukemi, gry zapaśnicze o pozycję i równowagę, gry w parterze i gry drużynowe – każda z grupą wiekową, czasem, zasadami, wariantami i zasadami bezpieczeństwa.',
  items: ['Gry ruchowe na rozgrzewkę', 'Zabawy z ukemi', 'Gry zapaśnicze o pozycję i równowagę', 'Gry w parterze', 'Zabawy dla przedszkolaków: zwierzęta na macie, tor przeszkód', 'Gry drużynowe'],
  outro: 'Dzieci nie czekają w kolejce – cała grupa jest w ruchu.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 04',
  level: 'Technika',
  title: 'Technika krok po kroku i program na pasy',
  tagline: 'Uczysz techniki bezpiecznie i w dobrej kolejności.',
  intro: 'Ukemi, postawa, chwyt (kumikata) i kuzushi, 10 rzutów dla dzieci i 4 trzymania z metodyką: krok po kroku, typowe błędy, korekta i ćwiczenia przygotowawcze.',
  items: ['4 rodzaje ukemi z progresją', 'Postawa, kumikata i kuzushi', '10 rzutów dla dzieci', '4 trzymania (osaekomi-waza) i wyjścia z trzymań', 'Typowe błędy i korekta', 'Program klubowy 6 kyu → 1 kyu z pasami pośrednimi'],
  outro: 'Bez dźwigni i duszeń – technika dobrana do wieku dzieci.',
  note: 'Program klubowy do dopasowania do regulaminu egzaminów Twojego klubu i PZJudo. To nie jest oficjalny program szkolenia Polskiego Związku Judo.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 05',
  level: 'Bezpieczeństwo',
  title: 'Bezpieczeństwo, prowadzenie grupy i rodzice',
  tagline: 'Spokojna, bezpieczna mata i dobra współpraca z rodzicami.',
  intro: 'Zasady na macie i w dojo, ustawienie par, praca z grupą mieszaną, dyscyplina bez krzyku, pierwsze zawody, obóz i postępowanie przy urazie.',
  items: ['Zasady na macie: rei, higiena, paznokcie, biżuteria', 'Bezpieczne odległości i ustawienie par', 'Praca z grupą mieszaną', 'Dyscyplina bez krzyku', 'Komunikacja z rodzicami', 'Pierwsze zawody, obóz i postępowanie przy urazie'],
  outro: 'Mniej chaosu na macie, więcej spokoju – dla Ciebie i dla rodziców.',
  note: 'Przy każdym urazie: zawsze lekarz lub ratownik. W razie urazu lub bólu skonsultuj się z lekarzem.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 06',
  level: 'Ocena i egzaminy',
  title: 'Ocena postępów, testy sprawności i egzaminy na pasy',
  tagline: 'Widzisz postępy każdego zawodnika.',
  intro: 'Testy sprawności ogólnej i specjalnej z tabelami punktowymi, karty egzaminacyjne na każdy pas, protokół egzaminu, dzienniczek zawodnika, dyplomy i 100 gotowych komentarzy.',
  items: ['Testy sprawności z tabelami punktowymi', 'Karty egzaminacyjne na każdy pas', 'Protokół egzaminu', 'Dzienniczek zawodnika i dyplomy', 'Rozmowa z rodzicem', '100 gotowych komentarzy dla zawodników i rodziców'],
  outro: 'Egzamin i ocena postępów – bez tworzenia dokumentów od zera.',
  note: DISCLAIMER,
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 cyfrowych tomów dla trenera judo',
  text: 'System sześć w jednym do prowadzenia zajęć judo z dziećmi i młodzieżą (4–15 lat) – przez cały sezon, od września do czerwca.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Gotowe zajęcia',
  text: '12 zajęć minuta po minucie i 50 treningów w banku zajęć – nie zaczynasz od pustej kartki.'
}, {
  icon: UsersIcon,
  title: '3 poziomy w każdych zajęciach',
  text: 'Jedna mata, różny wiek i różne pasy – każdy ćwiczy na swoim poziomie i nikt nie czeka.'
}, {
  icon: TimerIcon,
  title: '100 gier i zabaw',
  text: 'Rozgrzewki, zabawy z ukemi, gry zapaśnicze i gry w parterze, które angażują całą grupę.'
}, {
  icon: ShieldCheckIcon,
  title: 'Bezpieczeństwo na pierwszym miejscu',
  text: 'Ukemi od pierwszych zajęć, technika dobrana do wieku, bez dźwigni i duszeń.'
}, {
  icon: MedalIcon,
  title: 'Egzaminy na pasy',
  text: 'Program klubowy 6 kyu → 1 kyu, karty egzaminacyjne, protokoły i dyplomy.'
}, {
  icon: CalendarCheckIcon,
  title: 'Plan na cały sezon',
  text: 'Plan roczny wrzesień–czerwiec z egzaminami w grudniu i czerwcu oraz turniejami.'
}];

// Real Tom 1 sample pages live in data/samples.ts (empty = section hidden).
export const pageSamples = samples;
export const pageTypes = ['Plan roczny', 'Zajęcia minuta po minucie', 'Gry i zabawy', 'Karty do druku'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Zastępstwa bez stresu',
  text: '12 gotowych zajęć dla asystenta lub zastępcy – w trzech grupach wiekowych: 4–6, 7–10 i 11–15 lat.',
  items: ['12 gotowych zajęć', '3 grupy wiekowe', 'Łatwe do przekazania'],
  benefit: 'Spokój, gdy nie możesz poprowadzić treningu osobiście.'
}, {
  title: 'Rozgrzewki judo w 5 minut',
  text: '60 kart do druku: rozgrzewka, ukemi, gry i wyciszenie.',
  items: ['60 kart do druku', 'Rozgrzewka i ukemi', 'Gry i wyciszenie'],
  benefit: 'Sprawny start każdych zajęć.'
}, {
  title: 'Turniej klubowy i dzień judo krok po kroku',
  text: 'Listy kontrolne, losowanie, zasady dla dzieci, protokoły, dyplomy i zajęcia pokazowe dla rodziców.',
  items: ['Listy kontrolne i losowanie', 'Zasady dla dzieci i protokoły', 'Dyplomy', 'Zajęcia pokazowe dla rodziców'],
  benefit: 'Klubowe wydarzenie bez nerwów w ostatniej chwili.'
}, {
  title: 'Komunikacja z rodzicami',
  text: '40 gotowych wiadomości (e-mail, Messenger, WhatsApp, Librus): nabór, start sezonu, strój, egzamin, turniej, obóz, odwołane zajęcia i składki.',
  items: ['40 gotowych wiadomości', 'Nabór i start sezonu', 'Egzamin, turniej i obóz', 'Odwołane zajęcia i składki'],
  benefit: 'Mniej czasu na pisanie do rodziców.'
}, {
  title: 'Plakaty do dojo',
  text: '15 plakatów do druku: zasady na macie, rei, ukemi, kolory pasów i „Moje judo”.',
  items: ['15 plakatów do druku', 'Zasady na macie i rei', 'Ukemi i kolory pasów'],
  benefit: 'Dojo, które codziennie przypomina o zasadach.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Otwórz JUDO GOTOWE™',
  text: 'Pobierz pakiet i otwórz potrzebny tom.'
}, {
  title: 'Wybierz zajęcia',
  text: 'Wybierz zajęcia, grę lub ćwiczenie dla swojej grupy wiekowej i poziomu.'
}, {
  title: 'Wydrukuj',
  text: 'Wydrukuj plan albo miej go w telefonie przy macie.'
}, {
  title: 'Wejdź na matę',
  text: 'Prowadź zajęcia z gotowym planem.'
}];
export const audienceItems = ['Prowadzisz sekcję judo w klubie lub UKS', 'Uczysz judo w szkole lub w przedszkolu', 'Jesteś nauczycielem WF i prowadzisz zajęcia z judo lub zapasów', 'Masz grupy 4–6, 7–10 lub 11–15 lat – albo wszystkie naraz', 'Chcesz mieć plan na cały sezon, od września do czerwca', 'Nie chcesz co tydzień planować treningu od zera', 'Chcesz, żeby dzieci nie nudziły się na macie', 'Chcesz mieć pod ręką egzaminy, testy i wiadomości do rodziców'];
export const timeValueTasks = ['Planowanie każdego treningu od zera', 'Szukanie nowych gier i zabaw', 'Dopasowanie ćwiczeń do różnych grup i pasów', 'Przygotowanie egzaminów na pasy i dyplomów', 'Pisanie wiadomości do rodziców'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 cyfrowych tomów (240 stron)',
  detail: 'Plan roczny, bank zajęć, gry i zabawy, technika, bezpieczeństwo, ocena i egzaminy'
}, {
  label: '5 bonusów bez dodatkowych kosztów',
  detail: 'Zastępstwa, rozgrzewki, turniej klubowy, wiadomości do rodziców i plakaty do dojo'
}, {
  label: 'Dla grup 4–15 lat',
  detail: 'Zajęcia minuta po minucie w 3 poziomach'
}];
export const offerIncludes = ['Natychmiastowy dostęp do pobrania', 'Pliki PDF do zapisania', 'Materiały do druku', 'Nie wysyłamy produktu fizycznego'];
export const PRICE = '99 zł';
export const OLD_PRICE = '199 zł';

// No testimonials on this page.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
  topic: string;
}> = [];
export const faqs = [{
  q: 'Jak otrzymam materiały?',
  a: 'JUDO GOTOWE™ jest produktem cyfrowym. Po zakupie otrzymujesz dostęp do plików PDF, które możesz pobrać i zapisać. Możesz korzystać z nich na komputerze, tablecie lub telefonie i drukować to, czego potrzebujesz.'
}, {
  q: 'Czy to oficjalny program PZJudo?',
  a: 'Nie. JUDO GOTOWE™ to niezależny materiał dla trenerów. To nie jest oficjalna publikacja ani oficjalny program szkolenia Polskiego Związku Judo. Program na pasy to program klubowy, który dopasujesz do regulaminu egzaminów swojego klubu i PZJudo.'
}, {
  q: 'Dla jakiego wieku są zajęcia?',
  a: 'Dla dzieci i młodzieży od 4 do 15 lat, w trzech grupach: 4–6 lat (przedszkolaki, krótsze zajęcia oparte na zabawie), 7–10 lat i 11–15 lat. Każde zajęcia mają 3 poziomy, więc sprawdzą się także w grupie mieszanej.'
}, {
  q: 'Czy w programie są dźwignie i duszenia?',
  a: 'Nie. Ze względu na bezpieczeństwo dzieci materiały nie zawierają dźwigni (kansetsu-waza) ani duszeń (shime-waza). Skupiamy się na ukemi, trzymaniach (osaekomi-waza), podstawowych rzutach i wyjściach z trzymań, a randori odbywa się według jasnych zasad.'
}, {
  q: 'Czy to dla mnie, jeśli jestem nauczycielem WF?',
  a: 'Tak, jeśli prowadzisz zajęcia z judo lub zapasów w szkole. Materiały zakładają, że znasz podstawy judo i bezpiecznego padania (ukemi) – to gotowe plany dla prowadzącego, a nie kurs judo od zera.'
}, {
  q: 'Czy naprawdę przygotuję zajęcia w 5 minut?',
  a: 'JUDO GOTOWE™ powstało po to, by przyspieszyć przygotowanie treningu. Otwierasz tom, wybierasz gotowe zajęcia lub grę i zapoznajesz się z nimi w około pięć minut. Czas może się różnić w zależności od grupy i potrzebnych dostosowań.'
}, {
  q: 'Czy materiałów wystarczy na cały sezon?',
  a: 'Tak. Plan roczny obejmuje sezon od września do czerwca, a do tego masz 12 zajęć minuta po minucie, 50 treningów w banku zajęć i 100 gier i zabaw. To Ty wybierasz, łączysz i dostosowujesz je do swojej grupy.'
}, {
  q: 'Czy mogę wydrukować materiały?',
  a: 'Tak. Wszystko jest w PDF do druku: plany zajęć, karty gier, karty egzaminacyjne, dyplomy i plakaty. Nie musisz drukować wszystkiego – z części materiałów możesz korzystać na telefonie lub tablecie przy macie.'
}, {
  q: 'Czy pięć bonusów jest w cenie?',
  a: `Tak. Wszystkich pięć bonusów jest w pakiecie bez dodatkowych kosztów – całość za ${PRICE}.`
}, {
  q: 'Czy to subskrypcja?',
  a: 'Nie. Płacisz jednorazowo i otrzymujesz dostęp do materiałów cyfrowych. Bez żadnych opłat cyklicznych.'
}, {
  q: 'Co w razie urazu na macie?',
  a: 'Materiały zawierają zasady bezpieczeństwa i postępowania przy urazie, ale nie zastępują porady medycznej. W razie urazu lub bólu zawsze skonsultuj się z lekarzem lub wezwij ratownika.'
}, {
  q: 'Czy jest gwarancja?',
  a: 'Tak. JUDO GOTOWE™ objęte jest 7-dniową gwarancją satysfakcji. Jeśli materiały nie spełnią Twoich oczekiwań, możesz skontaktować się z nami i poprosić o zwrot pieniędzy w okresie gwarancji, zgodnie z jej warunkami.'
}];
export const footerLinks = ['Regulamin', 'Polityka prywatności', 'Polityka zwrotów', 'Kontakt'];
